/**
 * Pricing Table Component
 * 
 * Displays all available subscription plans in a responsive grid.
 */

import React, { useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import PlanCard from './PlanCard';
import { SubscriptionPlan } from '../../types/subscription';
import { useSubscription } from '../../context/subscription/SubscriptionContext';

interface PricingTableProps {
  plans: SubscriptionPlan[];
  currentPlanId?: string;
  onPlanSelect?: (planId: string) => void;
  showCurrentPlan?: boolean;
  className?: string;
}

const PricingTable: React.FC<PricingTableProps> = ({
  plans,
  currentPlanId,
  onPlanSelect,
  showCurrentPlan = true,
  className = ''
}) => {
  const { startCheckout, loading: subscriptionLoading, error } = useSubscription();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const handlePlanSelect = async (planId: string) => {
    if (onPlanSelect) {
      onPlanSelect(planId);
      return;
    }

    // Default behavior: start checkout
    try {
      setLoadingPlan(planId);
      await startCheckout(planId);
    } catch (err) {
      console.error('Failed to start checkout:', err);
    } finally {
      setLoadingPlan(null);
    }
  };

  // Filter out free plan and sort by price
  const sortedPlans = [...plans]
    .filter(plan => plan.id !== 'free')
    .sort((a, b) => a.price - b.price);

  if (subscriptionLoading && plans.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-gray-600">Loading pricing plans...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Failed to load pricing plans
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No pricing plans available at the moment.</p>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      {/* Header */}
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Choose Your Plan
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Start with our free trial and upgrade to unlock custom AI audiences and premium features. 
          All plans include our core simulation features.
        </p>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {sortedPlans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            isCurrentPlan={showCurrentPlan && plan.id === currentPlanId}
            currentPlanId={currentPlanId}
            onSelect={handlePlanSelect}
            loading={loadingPlan === plan.id}
            className="h-full"
          />
        ))}
      </div>


      {/* FAQ Section */}
      <div className="mt-16 max-w-3xl mx-auto">
        <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
          Frequently Asked Questions
        </h3>
        
        <div className="space-y-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">
              Can I change my plan anytime?
            </h4>
            <p className="text-sm text-gray-600">
              Yes! You can upgrade or downgrade your plan at any time. 
              Changes take effect immediately, and we'll prorate any charges.
            </p>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">
              What happens if I exceed my simulation limit?
            </h4>
            <p className="text-sm text-gray-600">
              Your simulations will be paused until your next billing cycle or until you upgrade. 
              We'll notify you when you're approaching your limit.
            </p>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">
              What's included in the Free Trial?
            </h4>
            <p className="text-sm text-gray-600">
              Our free trial gives you 1 simulation to try our platform with basic surveys. 
              You can upgrade at any time to unlock more simulations and premium features.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Sales */}
      <div className="mt-16 text-center">
        <div className="bg-gray-50 rounded-lg p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Need Enterprise Features?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Enterprise plans start at $17,999 annually plus $199 per seat per month. 
            Get unlimited simulations, custom data sets, and dedicated support.
          </p>
          <button 
            onClick={() => window.open('https://calendly.com/ad-blue-pill/30min', '_blank')}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-gray-900 hover:bg-gray-800">
            Let's Talk
          </button>
        </div>
      </div>

      {/* Subscription Cancellation */}
      <div className="mt-16 text-center max-w-3xl mx-auto">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          Need to Cancel Your Subscription?
        </h3>
        <p className="text-gray-600 mb-6">
          We're sorry to see you go! If you'd like to cancel your subscription or have any concerns, 
          please schedule a call with our team. We'll help with cancellation or address any issues you're experiencing.
        </p>
        <button 
          onClick={() => window.open('https://calendly.com/ad-blue-pill/30min', '_blank')}
          className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-gray-900 hover:bg-gray-800 transition-colors">
          Schedule Cancellation Call
        </button>
        <p className="text-sm text-gray-500 mt-3">
          Your subscription will remain active until we process your cancellation request.
        </p>
      </div>
    </div>
  );
};

export default PricingTable;